'use client';

import { useEffect, useState } from 'react';
import {
  LifeBuoy,
  X,
  ChevronRight,
  ChevronDown,
  CalendarClock,
  Plus,
  Inbox,
  PanelLeftOpen,
  Maximize2,
} from 'lucide-react';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { useSupportOpenCount } from '../hooks/use-support';
import { useBadgeCounts } from '@/features/notifications/hooks/use-notifications';
import { useFabHidden } from '../lib/use-fab-hidden';
import { SupportContactOptions } from './support-contact-options';
import { SupportTicketsList } from './support-tickets-list';
import { SupportTicketThread } from './support-ticket-thread';
import { NewTicketForm } from './new-ticket-form';
import { ScheduleCallEmbed } from './schedule-call-embed';
import { AdminInboxView } from './admin-inbox-view';
import { AdminThreadView } from './admin-thread-view';
import { isWithinSupportHours } from '../lib/support-config';

type View =
  | { kind: 'home' }
  | { kind: 'new' }
  | { kind: 'thread'; ticketId: string }
  | { kind: 'schedule' };

export function SupportWidget() {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>({ kind: 'home' });
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.isSuperAdmin === true;
  const { data: openCount } = useSupportOpenCount();
  const { data: badgeData } = useBadgeCounts();
  const adminUnreadCount = badgeData?.supportConversations ?? 0;
  const { hidden: fabHidden, hide: hideFab, show: showFab } = useFabHidden();
  const online = isWithinSupportHours();

  // Pentru admin: badge = conversatii cu mesaje necitite (zero cand toate
  // notificarile support_new_message sunt read). Pentru regular: tichetele
  // proprii deschise.
  const fabBadge = isAdmin ? adminUnreadCount : (openCount?.count ?? 0);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key === '/') {
        event.preventDefault();
        setOpen((current) => !current);
      } else if (event.key === 'Escape' && open) {
        setOpen(false);
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  // Cand FAB-ul e ascuns, butonul din sidebar emite acest event ca sa deschida
  // widget-ul (decoupling complet — widget-ul ramane self-contained).
  useEffect(() => {
    function onOpenRequest() {
      setOpen(true);
    }
    window.addEventListener('sf-support:open', onOpenRequest);
    return () => window.removeEventListener('sf-support:open', onOpenRequest);
  }, []);

  useEffect(() => {
    if (!open) {
      const t = window.setTimeout(() => setView({ kind: 'home' }), 200);
      return () => window.clearTimeout(t);
    }
  }, [open]);

  return (
    <>
      {!fabHidden && (
        <div className="group fixed bottom-5 right-5 z-40">
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-label="Suport"
            className="relative inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary-500 text-white shadow-[0_10px_30px_-8px_rgba(245,158,11,0.6),0_4px_10px_-4px_rgba(0,0,0,0.2)] transition-all hover:scale-105 hover:bg-primary-600 hover:shadow-[0_14px_36px_-10px_rgba(245,158,11,0.7),0_6px_14px_-6px_rgba(0,0,0,0.25)] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-neutral-950 sm:h-14 sm:w-14"
          >
            <LifeBuoy className="h-5 w-5 transition-transform group-hover:rotate-12 sm:h-6 sm:w-6" strokeWidth={1.75} />
            {fabBadge > 0 && (
              <span className={`absolute -top-1 -right-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1 text-[0.625rem] font-semibold ring-2 ${
                isAdmin
                  ? 'bg-red-500 text-white ring-white animate-pulse dark:ring-neutral-950'
                  : 'bg-neutral-900 text-white ring-white dark:bg-white dark:text-neutral-900 dark:ring-neutral-950'
              }`}>
                {fabBadge > 99 ? '99+' : fabBadge}
              </span>
            )}
          </button>

          {/* Mini-buton "ascunde" — vizibil pe hover, in coltul stanga-sus
              ca sa nu se suprapuna cu badge-ul de unread. */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              hideFab();
            }}
            aria-label="Ascunde butonul de suport"
            title="Mută în bara laterală"
            className="absolute -left-1 -top-1 inline-flex h-5 w-5 items-center justify-center rounded-full border border-neutral-200 bg-white text-neutral-500 opacity-0 shadow-sm transition-opacity hover:bg-neutral-50 hover:text-neutral-900 group-hover:opacity-100 focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-neutral-100"
          >
            <ChevronDown className="h-3 w-3" strokeWidth={2} />
          </button>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex justify-end" aria-modal="true" role="dialog">
          <button
            type="button"
            aria-label="Închide"
            onClick={() => setOpen(false)}
            className="absolute inset-0 cursor-default bg-neutral-950/30 backdrop-blur-sm transition-opacity duration-300 animate-[sw-fade_180ms_ease-out_both] dark:bg-neutral-950/50"
          />

          <div className="relative h-full w-full max-w-[440px] overflow-hidden bg-[#fdfaf4] shadow-[0_0_60px_-15px_rgba(0,0,0,0.3)] animate-[sw-slide_280ms_cubic-bezier(0.22,1,0.36,1)_both] dark:bg-neutral-950 sm:m-3 sm:h-auto sm:max-h-[calc(100vh-1.5rem)] sm:rounded-2xl sm:border sm:border-neutral-200/80 dark:sm:border-neutral-800/80">
            <div className="flex items-center justify-between border-b border-neutral-200/70 bg-white/40 px-4 py-3 backdrop-blur-sm dark:border-neutral-800/70 dark:bg-neutral-900/40">
              <div className="flex items-center gap-2">
                <div className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-primary-100 text-primary-700 dark:bg-primary-900/50 dark:text-primary-300">
                  {isAdmin ? (
                    <Inbox className="h-3.5 w-3.5" strokeWidth={1.75} />
                  ) : (
                    <LifeBuoy className="h-3.5 w-3.5" strokeWidth={1.75} />
                  )}
                </div>
                <div>
                  <div className="text-[0.8125rem] font-semibold text-neutral-900 dark:text-neutral-100">
                    {isAdmin ? 'Inbox suport' : 'Suport'}
                  </div>
                  <div className="flex items-center gap-1.5 text-[0.625rem] text-neutral-500 dark:text-neutral-500">
                    {isAdmin ? (
                      <>
                        <span
                          className={`inline-block h-1.5 w-1.5 rounded-full ${
                            adminUnreadCount > 0 ? 'bg-red-500 animate-pulse' : 'bg-emerald-500'
                          }`}
                        />
                        {adminUnreadCount > 0
                          ? `${adminUnreadCount} ${adminUnreadCount === 1 ? 'conversație nouă de citit' : 'conversații noi de citit'}`
                          : 'Niciun mesaj nou'}
                      </>
                    ) : (
                      <>
                        <span
                          className={`inline-block h-1.5 w-1.5 rounded-full ${
                            online ? 'bg-emerald-500' : 'bg-neutral-400'
                          }`}
                        />
                        {online ? 'Online · L-V 9-18' : 'Offline · răspuns în 24h'}
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => (fabHidden ? showFab() : hideFab())}
                  aria-label={fabHidden ? 'Mută înapoi în colț' : 'Mută în bara laterală'}
                  title={fabHidden ? 'Mută înapoi în colț' : 'Mută în bara laterală'}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-900 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-neutral-100"
                >
                  {fabHidden ? (
                    <Maximize2 className="h-3.5 w-3.5" strokeWidth={1.75} />
                  ) : (
                    <PanelLeftOpen className="h-4 w-4" strokeWidth={1.75} />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Închide"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-900 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-neutral-100"
                >
                  <X className="h-4 w-4" strokeWidth={1.75} />
                </button>
              </div>
            </div>

            <div className="flex h-[calc(100vh-49px)] flex-col sm:h-[640px] sm:max-h-[calc(100vh-1.5rem-49px)]">
              {view.kind === 'home' && (
                isAdmin ? (
                  <AdminInboxView
                    onSelectTicket={(id) => setView({ kind: 'thread', ticketId: id })}
                  />
                ) : (
                  <HomeView
                    onSelectTicket={(id) => setView({ kind: 'thread', ticketId: id })}
                    onNewTicket={() => setView({ kind: 'new' })}
                    onScheduleCall={() => setView({ kind: 'schedule' })}
                  />
                )
              )}

              {view.kind === 'new' && !isAdmin && (
                <NewTicketForm
                  onBack={() => setView({ kind: 'home' })}
                  onCreated={(id) => setView({ kind: 'thread', ticketId: id })}
                />
              )}

              {view.kind === 'thread' && (
                isAdmin ? (
                  <AdminThreadView
                    ticketId={view.ticketId}
                    onBack={() => setView({ kind: 'home' })}
                    onResolved={() => setView({ kind: 'home' })}
                  />
                ) : (
                  <SupportTicketThread
                    ticketId={view.ticketId}
                    onBack={() => setView({ kind: 'home' })}
                  />
                )
              )}

              {view.kind === 'schedule' && !isAdmin && (
                <ScheduleView onBack={() => setView({ kind: 'home' })} />
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes sw-fade {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes sw-slide {
          from {
            opacity: 0;
            transform: translateX(24px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </>
  );
}

interface HomeViewProps {
  onSelectTicket: (id: string) => void;
  onNewTicket: () => void;
  onScheduleCall: () => void;
}

function HomeView({ onSelectTicket, onNewTicket, onScheduleCall }: HomeViewProps) {
  return (
    <div className="flex-1 space-y-5 overflow-y-auto px-4 py-4">
      <section>
        <h3 className="mb-2 text-[0.625rem] font-semibold uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-500">
          Cum te putem ajuta
        </h3>
        <SupportContactOptions
          onStartLiveChat={onNewTicket}
          onScheduleCall={onScheduleCall}
        />
      </section>

      <section>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-[0.625rem] font-semibold uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-500">
            Tichetele mele
          </h3>
          <a
            href="/support"
            className="inline-flex items-center gap-0.5 text-[0.6875rem] font-medium text-primary-700 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
          >
            Vezi toate
            <ChevronRight className="h-3 w-3" strokeWidth={2} />
          </a>
        </div>
        <SupportTicketsList onSelectTicket={onSelectTicket} limit={3} />
        <button
          type="button"
          onClick={onNewTicket}
          className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-neutral-300 px-3 py-2 text-[0.75rem] font-medium text-neutral-600 transition-colors hover:border-primary-300 hover:bg-primary-50/40 hover:text-primary-700 dark:border-neutral-700 dark:text-neutral-400 dark:hover:border-primary-700/50 dark:hover:bg-primary-900/20 dark:hover:text-primary-300"
        >
          <Plus className="h-3.5 w-3.5" strokeWidth={1.75} />
          Tichet nou
        </button>
      </section>

      <footer className="border-t border-neutral-200/70 pt-3 text-center text-[0.6875rem] text-neutral-400 dark:border-neutral-800/70 dark:text-neutral-600">
        <a
          href="/ajutor"
          className="hover:text-neutral-700 dark:hover:text-neutral-300"
        >
          Centru de ajutor (95+ articole) →
        </a>
      </footer>
    </div>
  );
}

interface ScheduleViewProps {
  onBack: () => void;
}

function ScheduleView({ onBack }: ScheduleViewProps) {
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-b border-neutral-200 px-4 py-3 dark:border-neutral-800">
        <button
          type="button"
          onClick={onBack}
          aria-label="Înapoi"
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-900 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-neutral-100"
        >
          <X className="h-4 w-4 rotate-45" strokeWidth={1.75} />
        </button>
        <div className="flex items-center gap-2 text-[0.875rem] font-medium text-neutral-900 dark:text-neutral-100">
          <CalendarClock className="h-4 w-4 text-violet-600" strokeWidth={1.75} />
          Programează un apel
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-3">
        <ScheduleCallEmbed />
      </div>
    </div>
  );
}
